import { useState } from "react"
import { View, ActivityIndicator, Pressable } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { SafeAreaView } from "react-native-safe-area-context"
import { Link, useLocalSearchParams, useRouter } from "expo-router"

import { ar } from "i18n/ar"
import { sharedStyles, formatDateForDisplay, trpc, localDateToUnix, unixToLocalDate, formatPrice } from "@src/utils"
import { Header, Text, DatePicker, List, SupplierPicker, ModalWrapper } from "@src/components"

export default function PaymentsScreen() {
  const router = useRouter()
  const params = useLocalSearchParams<{ supplierId?: string; startDate?: string; endDate?: string }>()
  const [showFilters, setShowFilters] = useState(false)

  const supplierId = params.supplierId ? Number(params.supplierId) : undefined
  // filters start undefined: all payments are shown until the user applies a filter
  const startDate = params.startDate ? Number(params.startDate) : undefined
  const endDate = params.endDate ? Number(params.endDate) : undefined

  const payments = trpc.payments.list.useInfiniteQuery(
    { supplierId, startDate, endDate },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  )

  const allPayments = payments.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <>
      <Header
        title={ar.payments}
        right={
          <View className="h-full flex-row">
            <Pressable onPress={() => setShowFilters(true)} className="p-2 h-full justify-center active:opacity-40">
              <Icon name="filter" size={24} color="#000" />
            </Pressable>
            <Link href={supplierId ? `/dashboard/payments/new?supplierId=${supplierId}` : "/dashboard/payments/new"} asChild>
              <Pressable className="p-2 h-full justify-center active:opacity-40">
                <Icon name="plus" size={24} color="#000" />
              </Pressable>
            </Link>
          </View>
        }
      />

      <List
        data={allPayments}
        contentContainerStyle={sharedStyles.contentContainer}
        ListHeaderComponent={
          <View className="hidden md:flex flex-row px-6 py-3 bg-gray-50 border-y border-gray-200">
            <Text className="flex-1 text-gray-500 font-medium">{ar.supplier}</Text>
            <Text className="w-48 text-gray-500 font-medium">{ar.date}</Text>
            <Text className="w-32 text-right text-gray-500 font-medium">{ar.amount}</Text>
          </View>
        }
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item: payment }) => {
          // the user-chosen payment date, falling back to createdAt for legacy rows
          const paymentDate = payment.date ?? payment.createdAt
          return (
            <Pressable className="bg-white md:bg-transparent p-2 md:px-3 md:py-2 mx-2 md:mx-0 mb-1.5 md:mb-0 rounded md:rounded-none border border-gray-100 md:border-x-0 md:border-t-0 md:border-b shadow-sm md:shadow-none hover:bg-gray-50">
              {/* Mobile */}
              <View className="md:hidden">
                <View className="flex-row justify-between items-start mb-1.5">
                  <View className="flex-row items-center flex-1 gap-1.5">
                    <View className="w-10 h-10 rounded-full bg-blue-50 items-center justify-center">
                      <Icon name="account" size={20} color="#2563EB" />
                    </View>
                    <View className="flex-1">
                      <Text className="font-bold text-gray-900 text-base mb-0.5">{payment.supplier?.name || ar.noSupplier}</Text>
                      <Text className="text-xs text-gray-500">{formatDateForDisplay(paymentDate)}</Text>
                      <Text className="text-xs text-gray-400 mt-0.5">
                        {ar.recordedAt}: {formatDateForDisplay(payment.createdAt)}
                      </Text>
                    </View>
                  </View>
                  <View className="bg-green-50 px-1.5 py-1 rounded">
                    <Text className="font-bold text-green-700">
                      {formatPrice(payment.amount)} {ar.egp}
                    </Text>
                  </View>
                </View>
                {payment.notes && (
                  <View className="bg-gray-50 p-1.5 rounded">
                    <Text className="text-sm text-gray-600">{payment.notes}</Text>
                  </View>
                )}
              </View>

              {/* Desktop */}
              <View className="hidden md:flex md:flex-row md:items-center">
                <View className="flex-1 pr-2">
                  <Text className="font-bold text-gray-900 text-base mb-0.5">{payment.supplier?.name || ar.noSupplier}</Text>
                  {payment.notes && (
                    <Text className="text-sm text-gray-500" numberOfLines={1}>
                      {payment.notes}
                    </Text>
                  )}
                </View>
                <View className="w-48">
                  <View className="flex-row items-center gap-1">
                    <Icon name="calendar" size={16} color="#9CA3AF" />
                    <Text className="text-sm text-gray-600">{formatDateForDisplay(paymentDate)}</Text>
                  </View>
                  <Text className="text-xs text-gray-400 mt-0.5">
                    {ar.recordedAt}: {formatDateForDisplay(payment.createdAt)}
                  </Text>
                </View>
                <Text className="w-32 font-bold text-green-600 text-base">
                  {formatPrice(payment.amount)} {ar.egp}
                </Text>
              </View>
            </Pressable>
          )
        }}
        onRefresh={payments.refetch}
        onEndReached={() => {
          if (payments.hasNextPage && !payments.isFetchingNextPage) payments.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={
          payments.isLoading ? (
            <View className="bg-white p-6 rounded shadow-sm items-center mx-2 md:mx-0">
              <ActivityIndicator size="large" color="#3B82F6" />
              <Text className="text-gray-500 mt-2 font-medium">{ar.loading}</Text>
            </View>
          ) : (
            <View className="bg-white p-6 rounded shadow-sm items-center mx-2 md:mx-0">
              <View className="w-16 h-16 bg-gray-50 rounded-full items-center justify-center mb-2">
                <Icon name="receipt" size={32} color="#9CA3AF" />
              </View>
              <Text className="text-gray-900 font-bold text-lg mb-0.5">{ar.noPaymentsFound}</Text>
              <Text className="text-gray-500 text-center">{ar.tryAdjustFiltersOrAddNewPayment}</Text>
            </View>
          )
        }
        ListFooterComponent={
          <View>
            {payments.isFetchingNextPage && (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" color="#3B82F6" />
              </View>
            )}
            <SafeAreaView edges={["bottom"]} />
          </View>
        }
      />

      {showFilters && (
        <FiltersModal
          hide={() => setShowFilters(false)}
          initial={{ supplierId, startDate, endDate }}
          onApply={(f) => router.setParams({ supplierId: f.supplierId?.toString(), startDate: f.startDate?.toString(), endDate: f.endDate?.toString() })}
        />
      )}
    </>
  )
}

type Filters = { supplierId?: number; startDate?: number; endDate?: number }

function FiltersModal({ hide, initial, onApply }: { hide: () => void; initial: Filters; onApply: (f: Filters) => void }) {
  const [supplierId, setSupplierId] = useState(initial.supplierId)
  const [startDate, setStartDate] = useState(initial.startDate ? unixToLocalDate(initial.startDate) : undefined)
  const [endDate, setEndDate] = useState(initial.endDate ? unixToLocalDate(initial.endDate) : undefined)

  function apply() {
    const start = startDate ? new Date(startDate.setHours(0, 0, 0, 0)) : undefined
    const end = endDate ? new Date(endDate.setHours(23, 59, 59, 999)) : undefined
    onApply({ supplierId, startDate: start ? localDateToUnix(start) : undefined, endDate: end ? localDateToUnix(end) : undefined })
    hide()
  }

  return (
    <ModalWrapper onClose={hide}>
      <View className="flex-row items-center justify-between mb-6">
        <Text className="text-xl font-bold">{ar.filters}</Text>
        <Pressable onPress={hide} hitSlop={20} className="bg-gray-100 p-2 rounded-full active:bg-gray-200">
          <Icon name="close" size={20} color="#374151" />
        </Pressable>
      </View>

      <View className="mb-6">
        <Text className="text-sm font-bold text-gray-900 mb-3">{ar.supplier}</Text>
        <SupplierPicker value={supplierId ? [supplierId] : []} onSelectionChange={(s) => setSupplierId(s[0])} />
      </View>

      <View className="mb-8">
        <Text className="text-sm font-bold text-gray-900 mb-3">{ar.date}</Text>
        <View className="flex-row gap-3">
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1.5 font-medium">{ar.from}</Text>
            <View className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <DatePicker value={startDate} onChange={setStartDate} mode="date" placeholder={ar.from} />
            </View>
          </View>
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1.5 font-medium">{ar.to}</Text>
            <View className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <DatePicker value={endDate} onChange={setEndDate} mode="date" placeholder={ar.to} />
            </View>
          </View>
        </View>
      </View>

      <View className="flex-row gap-3">
        <Pressable
          onPress={() => {
            onApply({})
            hide()
          }}
          className="flex-1 py-2.5 border border-gray-300 rounded-lg active:bg-gray-50"
        >
          <Text className="text-center text-gray-700 font-bold">{ar.clearFilters}</Text>
        </Pressable>
        <Pressable onPress={apply} className="flex-1 py-2.5 bg-blue-600 rounded-lg active:opacity-90 shadow-sm">
          <Text className="text-center text-white font-bold">{ar.apply}</Text>
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
